import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import i18n from './i18n';

/**
 * Formats de date prédéfinis selon la locale
 */
export const DATE_FORMATS = {
  // Date courte : 01/12/2024 (FR) ou 12/01/2024 (US)
  SHORT: 'short',
  // Date moyenne : 1 déc. 2024 (FR) ou Dec 1, 2024 (US)
  MEDIUM: 'medium',
  // Date longue : 1 décembre 2024 (FR) ou December 1, 2024 (US)
  LONG: 'long',
  // Date complète avec heure : 01/12/2024 14:30 (FR) ou 12/01/2024 2:30 PM (US)
  DATETIME: 'datetime',
  // Date ISO : 2024-12-01 (identique pour toutes les locales)
  ISO: 'iso',
  // Date relative : Il y a 2 jours / 2 days ago
  RELATIVE: 'relative',
};

/**
 * Obtient la locale actuelle depuis i18n
 * @returns {string} 'fr' ou 'en'
 */
export const getCurrentLocale = () => {
  const lang = i18n.language || localStorage.getItem('i18nextLng') || 'en';
  return lang.startsWith('fr') ? 'fr' : 'en';
};

/**
 * Formate une date selon la locale et le format spécifié
 * @param {string|Date|dayjs.Dayjs} date - Date à formater
 * @param {string} format - Format à utiliser (voir DATE_FORMATS)
 * @param {string} customFormat - Format personnalisé dayjs (optionnel)
 * @returns {string} Date formatée
 */
export const formatDate = (date, format = DATE_FORMATS.SHORT, customFormat = null) => {
  if (!date) return '--';
  
  const locale = getCurrentLocale();
  const dayjsDate = dayjs(date);
  
  if (!dayjsDate.isValid()) return '--';
  
  // Si un format personnalisé est fourni, l'utiliser
  if (customFormat) {
    return dayjsDate.format(customFormat);
  }
  
  // Formats prédéfinis selon la locale
  switch (format) {
    case DATE_FORMATS.SHORT:
      // FR: DD/MM/YYYY, US: MM/DD/YYYY
      return locale === 'fr' 
        ? dayjsDate.format('DD/MM/YYYY')
        : dayjsDate.format('MM/DD/YYYY');
    
    case DATE_FORMATS.MEDIUM:
      // FR: D MMM YYYY, US: MMM D, YYYY
      return locale === 'fr'
        ? dayjsDate.format('D MMM YYYY')
        : dayjsDate.format('MMM D, YYYY');
    
    case DATE_FORMATS.LONG:
      // FR: D MMMM YYYY, US: MMMM D, YYYY
      return locale === 'fr'
        ? dayjsDate.format('D MMMM YYYY')
        : dayjsDate.format('MMMM D, YYYY');
    
    case DATE_FORMATS.DATETIME:
      // FR: DD/MM/YYYY HH:mm, US: MM/DD/YYYY h:mm A
      return locale === 'fr'
        ? dayjsDate.format('DD/MM/YYYY HH:mm')
        : dayjsDate.format('MM/DD/YYYY h:mm A');
    
    case DATE_FORMATS.ISO:
      // Format ISO identique pour toutes les locales
      return dayjsDate.format('YYYY-MM-DD');
    
    case DATE_FORMATS.RELATIVE:
      // Format relatif : Il y a X jours / X days ago
      // Nécessite le plugin relativeTime de dayjs
      return dayjsDate.fromNow();
    
    default:
      // Par défaut, utiliser le format court
      return locale === 'fr'
        ? dayjsDate.format('DD/MM/YYYY')
        : dayjsDate.format('MM/DD/YYYY');
  }
};

/**
 * Formate une date pour l'affichage dans un tableau (format court)
 * EN: "Dec 14, 25" (MMM D, YY)
 * FR: "14 déc. 25" (D MMM YY)
 * @param {string|Date|dayjs.Dayjs} date - Date à formater
 * @returns {string} Date formatée
 */
export const formatTableDate = (date) => {
  if (!date) return '--';
  
  const locale = getCurrentLocale();
  const dayjsDate = dayjs(date);
  
  if (!dayjsDate.isValid()) return '--';
  
  // Set locale for dayjs to ensure correct month names
  dayjsDate.locale(locale);
  
  // EN: "Dec 14, 25" (MMM D, YY)
  // FR: "14 déc. 25" (D MMM YY)
  return locale === 'fr'
    ? dayjsDate.format('D MMM YY')
    : dayjsDate.format('MMM D, YY');
};

/**
 * Mois calendaire YYYY-MM pour tableaux (liste CRA, validation client, export).
 * FR: "mars 2026", EN: "March 2026" — même locale que formatTableDate.
 * @param {string} yearMonth - Chaîne "YYYY-MM"
 * @returns {string}
 */
export const formatTableYearMonth = (yearMonth) => {
  if (!yearMonth || !/^\d{4}-\d{2}$/.test(String(yearMonth).trim())) return '--';

  const locale = getCurrentLocale();
  const d = dayjs(`${String(yearMonth).trim()}-01`);
  if (!d.isValid()) return '--';

  return d.locale(locale).format('MMMM YYYY');
};

/**
 * Formate une date pour l'affichage détaillé (format long)
 * @param {string|Date|dayjs.Dayjs} date - Date à formater
 * @returns {string} Date formatée
 */
export const formatDetailedDate = (date) => {
  return formatDate(date, DATE_FORMATS.LONG);
};

/** Sidebar — date courte localisée : « 07/09/26 » (FR) / « 09/07/26 » (EN) */
export const formatMissionSidebarCreatedDate = (date) => {
  if (!date) return '--';

  const locale = getCurrentLocale();
  const dayjsDate = dayjs(date);

  if (!dayjsDate.isValid()) return '--';

  return dayjsDate.locale(locale).format(locale === 'fr' ? 'DD/MM/YY' : 'MM/DD/YY');
};

/**
 * Formate une date avec l'heure
 * @param {string|Date|dayjs.Dayjs} date - Date à formater
 * @returns {string} Date et heure formatées
 */
export const formatDateTime = (date) => {
  return formatDate(date, DATE_FORMATS.DATETIME);
};

/**
 * Formate une date relative (Il y a X jours)
 * @param {string|Date|dayjs.Dayjs} date - Date à formater
 * @returns {string} Date relative formatée
 */
export const formatRelativeDate = (date) => {
  return formatDate(date, DATE_FORMATS.RELATIVE);
};

